/** Canonical pipeline stage enum — mirrors functions/src/pipelineStages.ts */

export const PIPELINE_STAGE = {
  UPLOADING: 'uploading',
  STITCHING_360: 'stitching_360',
  ANALYZING_FRAMES: 'analyzing_frames',
  BUILDING_STRUCTURE: 'building_structure',
  RENDERING: 'rendering',
  DONE: 'done',
  COMPLETE: 'complete',
  FAILED: 'failed',
};

export const VALID_PIPELINE_STAGES = new Set(Object.values(PIPELINE_STAGE));

export const PIPELINE_INTERNAL_TO_CANONICAL = {
  upload: PIPELINE_STAGE.UPLOADING,
  uploadpending: PIPELINE_STAGE.UPLOADING,
  stitching360: PIPELINE_STAGE.STITCHING_360,
  stitching: PIPELINE_STAGE.STITCHING_360,
  frameextraction: PIPELINE_STAGE.ANALYZING_FRAMES,
  'frameextraction-retrain': PIPELINE_STAGE.ANALYZING_FRAMES,
  frameextractionretrain: PIPELINE_STAGE.ANALYZING_FRAMES,
  sfm: PIPELINE_STAGE.BUILDING_STRUCTURE,
  'sfm-retrain': PIPELINE_STAGE.BUILDING_STRUCTURE,
  sfmretrain: PIPELINE_STAGE.BUILDING_STRUCTURE,
  sfmcomplete: PIPELINE_STAGE.BUILDING_STRUCTURE,
  gaussiantraining: PIPELINE_STAGE.RENDERING,
  'gaussiantraining-retrain': PIPELINE_STAGE.RENDERING,
  gaussiantrainingretrain: PIPELINE_STAGE.RENDERING,
  gaussianexport: PIPELINE_STAGE.RENDERING,
  sogconversion: PIPELINE_STAGE.RENDERING,
  finalizing: PIPELINE_STAGE.RENDERING,
  done: PIPELINE_STAGE.DONE,
  complete: PIPELINE_STAGE.COMPLETE,
};

export function normalizePipelineStage(raw) {
  if (raw == null) return null;
  const s = String(raw).trim().toLowerCase();
  if (VALID_PIPELINE_STAGES.has(s)) return s;
  const compact = s.replace(/[\s_-]/g, '');
  return PIPELINE_INTERNAL_TO_CANONICAL[compact] ?? null;
}

/** Failed/cancelled are terminal — they must not count as live worker/stage evidence. */
export function isTerminalPipelineStage(stage) {
  if (stage == null || stage === '') return false;
  const n = normalizePipelineStage(stage);
  if (n === PIPELINE_STAGE.FAILED) return true;
  const s = String(stage).trim().toLowerCase();
  return s === 'failed' || s === 'cancelled';
}

export function pipelineStageToSegmentIndex(stage) {
  switch (stage) {
  case PIPELINE_STAGE.UPLOADING:
    return 0;
  case PIPELINE_STAGE.STITCHING_360:
  case PIPELINE_STAGE.ANALYZING_FRAMES:
    return 1;
  case PIPELINE_STAGE.BUILDING_STRUCTURE:
    return 2;
  case PIPELINE_STAGE.RENDERING:
    return 3;
  case PIPELINE_STAGE.DONE:
  case PIPELINE_STAGE.COMPLETE:
    return 4;
  default:
    return -1;
  }
}

export function failedStageIndexToPipelineStage(idx) {
  if (!Number.isFinite(idx)) return null;
  if (idx >= 1 && idx <= 5) {
    switch (idx) {
    case 1: return PIPELINE_STAGE.UPLOADING;
    case 2: return PIPELINE_STAGE.ANALYZING_FRAMES;
    case 3: return PIPELINE_STAGE.BUILDING_STRUCTURE;
    case 4:
    case 5: return PIPELINE_STAGE.RENDERING;
    default: return null;
    }
  }
  switch (idx) {
  case 0: return PIPELINE_STAGE.UPLOADING;
  case 1: return PIPELINE_STAGE.ANALYZING_FRAMES;
  case 2: return PIPELINE_STAGE.BUILDING_STRUCTURE;
  case 3: return PIPELINE_STAGE.RENDERING;
  default: return null;
  }
}

export const FRIENDLY_STAGE_LABELS = [
  'Video Upload',
  'Image Extraction',
  'Analyzing Images',
  'Creating 3D Model',
];

function extractBackendLogBracketTag(message) {
  if (typeof message !== 'string') return null;
  const match = message.match(/\[([^\]]+)\]/);
  if (!match) return null;
  return match[1].trim().toUpperCase();
}

export function progressMessageForStage(stage) {
  switch (stage) {
  case PIPELINE_STAGE.UPLOADING:
    return 'UPLOADING VIDEO';
  case PIPELINE_STAGE.STITCHING_360:
    return 'STITCHING 360 VIDEO';
  case PIPELINE_STAGE.ANALYZING_FRAMES:
    return 'IMAGE EXTRACTION';
  case PIPELINE_STAGE.BUILDING_STRUCTURE:
    return 'ANALYZING IMAGES';
  case PIPELINE_STAGE.RENDERING:
    return 'CREATING 3D MODEL';
  case PIPELINE_STAGE.COMPLETE:
  case PIPELINE_STAGE.DONE:
    return 'RECONSTRUCTION COMPLETE';
  default:
    return null;
  }
}

export function friendlyStageLabel(stage) {
  const idx = pipelineStageToSegmentIndex(stage);
  if (idx >= 0 && idx < FRIENDLY_STAGE_LABELS.length) {
    return FRIENDLY_STAGE_LABELS[idx];
  }
  if (stage === PIPELINE_STAGE.DONE || stage === PIPELINE_STAGE.COMPLETE) return 'Complete';
  if (stage === PIPELINE_STAGE.FAILED) return 'Failed';
  return 'Processing';
}

/** Legacy fallback: infer stage from holistic percent + message when processing.stage absent. */
export function inferPipelineStageFromLegacyProgress(percent, message) {
  const m = (message || '').toLowerCase();
  const pct = Number.isFinite(Number(percent)) ? Math.max(0, Math.min(100, Number(percent))) : null;
  const bracketTag = extractBackendLogBracketTag(message);
  if (bracketTag === 'FRAME EXTRACTION') return PIPELINE_STAGE.ANALYZING_FRAMES;
  if (bracketTag === 'STITCH') return PIPELINE_STAGE.STITCHING_360;
  if (bracketTag === 'SFM') return PIPELINE_STAGE.BUILDING_STRUCTURE;
  // Strict backend tag contract: [GAUSSIAN] and any other backend bracket tag => RENDERING.
  if (bracketTag) return PIPELINE_STAGE.RENDERING;
  if (pct >= 92 || m.includes('finalizing') || m.includes('viewer ready')) {
    return PIPELINE_STAGE.RENDERING;
  }
  if (pct >= 51 || m.includes('generating gaussians') || (m.includes('gaussian') && !m.includes('sfm'))) {
    return PIPELINE_STAGE.RENDERING;
  }
  if (
    (pct != null && pct >= 5 && pct <= 50)
    || m.includes('structure from motion')
    || m.includes('running 3d reconstruction')
    || (m.includes('sfm') && !m.includes('gaussian'))
  ) {
    return PIPELINE_STAGE.BUILDING_STRUCTURE;
  }
  if (
    m.includes('extracting frames')
    || m.includes('extracting images')
    || m.includes('stitching 360')
    || (m.includes('frame') && m.includes('extract'))
    || (m.includes('image') && m.includes('extract'))
    || (pct != null && pct >= 3 && pct < 5)
  ) {
    return PIPELINE_STAGE.ANALYZING_FRAMES;
  }
  if (
    m.includes('uploading video')
    || m.includes('upload complete')
    || (pct != null && pct >= 2 && pct < 3)
  ) {
    return PIPELINE_STAGE.UPLOADING;
  }
  if (pct >= 100) return PIPELINE_STAGE.DONE;
  return null;
}

export function resolveCapturePipelineStage(capture, { isClientUploading = false } = {}) {
  const proc = capture?.processing || {};
  const fromField = normalizePipelineStage(proc.stage);
  if (isClientUploading) {
    // During upload->pipeline handoff, client state can lag one tick. If the backend has
    // already advanced the stage, prefer that to avoid "UPLOADING VIDEO" mismatches.
    if (fromField && fromField !== PIPELINE_STAGE.UPLOADING) return fromField;
  }
  if (fromField) return fromField;
  const pollStage = normalizePipelineStage(capture?.pollPipelineStage);
  if (isClientUploading) {
    if (pollStage && pollStage !== PIPELINE_STAGE.UPLOADING) return pollStage;
  }
  if (pollStage) return pollStage;
  const legacy = inferPipelineStageFromLegacyProgress(
    capture?.progress?.percent,
    capture?.progress?.message,
  );
  if (isClientUploading) {
    if (legacy && legacy !== PIPELINE_STAGE.UPLOADING) return legacy;
    return PIPELINE_STAGE.UPLOADING;
  }
  if (legacy) return legacy;
  const status = String(proc.status || '').toLowerCase();
  if (status === 'completed') {
    const stage = String(proc.stage || '').toLowerCase();
    if (stage === 'complete') return PIPELINE_STAGE.COMPLETE;
    return PIPELINE_STAGE.DONE;
  }
  if (status === 'failed' || status === 'cancelled') return PIPELINE_STAGE.FAILED;
  return null;
}
